import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { getSocket } from "../api/socket";
import client from "../api/client";

interface ChatMsg {
  id: string;
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  content: string;
  type: "message" | "solve" | "first_blood" | "join" | "system";
  timestamp: Date | string;
  meta?: {
    challengeTitle?: string;
    challengeId?: string;
    solveTime?: string;
    solveTimeMs?: number;
    points?: number;
    category?: string;
    difficulty?: string;
  };
}

interface OnlineUser {
  id: string;
  username: string;
  displayName: string | null;
}

const ASCII_HEADER = `
 ██████╗████████╗██████╗  ██████╗██╗  ██╗██╗   ██╗██████╗
██╔════╝╚══██╔══╝██╔══██╗██╔════╝██║  ██║██║   ██║██╔══██╗
██║        ██║   ██████╔╝██║     ███████║██║   ██║██████╔╝
██║        ██║   ██╔══██╗██║     ██╔══██║██║   ██║██╔═══╝
╚██████╗   ██║   ██║  ██║╚██████╗██║  ██║╚██████╔╝██║
 ╚═════╝   ╚═╝   ╚═╝  ╚═╝ ╚═════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝`;

const BOOT_LINES = [
  { text: "[INIT] CyberSentinel Terminal v2.0.26", delay: 0 },
  { text: "[SCAN] Establishing secure channel...", delay: 150 },
  { text: "[AUTH]  Verifying operator credentials...", delay: 300 },
  { text: "[CONN]  Socket.IO bridge online", delay: 500 },
  { text: "[LOAD]  Chat subsystem initialized", delay: 650 },
  { text: "[DONE]  Connection established. Welcome, operator.", delay: 800 },
];

const CATEGORY_COLORS: Record<string, string> = {
  web: "#5b7cff",
  crypto: "#a855f7",
  forensics: "#22d3ee",
  reverse: "#f97316",
  pwn: "#ef4444",
  misc: "#6b7280",
  osint: "#10b981",
  blockchain: "#eab308",
};

function getCatColor(cat: string): string {
  return CATEGORY_COLORS[cat.toLowerCase()] || "#5b7cff";
}

function getDiffBadge(diff: string) {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    EASY: { bg: "bg-emerald-500/15", text: "text-emerald-400", label: "EASY" },
    MEDIUM: { bg: "bg-yellow-500/15", text: "text-yellow-400", label: "MED" },
    HARD: { bg: "bg-orange-500/15", text: "text-orange-400", label: "HARD" },
    INSANE: { bg: "bg-red-500/15", text: "text-red-400", label: "INSANE" },
  };
  return map[diff] || map.EASY;
}

function formatTime(date: Date | string) {
  const d = new Date(date);
  return d.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function formatSolveTime(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

export default function TerminalChat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [typing, setTyping] = useState<string | null>(null);
  const [booted, setBooted] = useState(false);
  const [bootLines, setBootLines] = useState<string[]>([]);
  const [connected, setConnected] = useState(false);
  const [showOnline, setShowOnline] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const typingTimeout = useRef<ReturnType<typeof setTimeout>>();

  // Boot sequence
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      for (const line of BOOT_LINES) {
        await new Promise((r) => setTimeout(r, line.delay));
        if (cancelled) return;
        setBootLines((p) => [...p, line.text]);
      }
      await new Promise((r) => setTimeout(r, 200));
      if (!cancelled) setBooted(true);
    };
    run();
    return () => { cancelled = true; };
  }, []);

  // Load history + socket
  useEffect(() => {
    if (!booted || !user) return;

    const socket = getSocket();

    // Load history
    client.get("/chat/messages?limit=100").then((res) => {
      if (res.data?.success) {
        setMessages(res.data.data.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })));
      }
    }).catch(() => {});

    // Join chat room
    socket.emit("chat:join", { username: user.username, displayName: user.displayName });

    const onMessage = (msg: ChatMsg) => {
      setMessages((prev) => [...prev, { ...msg, timestamp: new Date(msg.timestamp) }]);
    };

    const onSystem = (evt: { type: string; message: string; timestamp: string; meta?: any }) => {
      setMessages((prev) => [
        ...prev,
        {
          id: `sys-${Date.now()}`,
          userId: "system",
          username: "SYSTEM",
          displayName: null,
          avatarUrl: null,
          content: evt.message,
          type: evt.type as any,
          timestamp: new Date(evt.timestamp),
          meta: evt.meta,
        },
      ]);
    };

    const onOnline = (data: { count: number; users: OnlineUser[] }) => {
      setOnlineUsers(data.users);
    };

    const onTyping = (data: { username: string; isTyping: boolean }) => {
      if (data.isTyping && data.username !== user.username) {
        setTyping(data.username);
      } else {
        setTyping(null);
      }
    };

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    socket.on("chat:message", onMessage);
    socket.on("chat:system", onSystem);
    socket.on("chat:online", onOnline);
    socket.on("chat:typing", onTyping);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    if (socket.connected) setConnected(true);

    return () => {
      socket.off("chat:message", onMessage);
      socket.off("chat:system", onSystem);
      socket.off("chat:online", onOnline);
      socket.off("chat:typing", onTyping);
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [booted, user]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = useCallback(() => {
    if (!input.trim() || !user) return;
    const socket = getSocket();
    socket.emit("chat:send", { content: input.trim() });
    setInput("");
    socket.emit("chat:typing", false);
  }, [input, user]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInput = (val: string) => {
    setInput(val);
    const socket = getSocket();
    socket.emit("chat:typing", true);
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socket.emit("chat:typing", false);
    }, 2000);
  };

  // ── Boot screen ──
  if (!booted) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-[#050a0f]">
        <div className="w-full max-w-2xl px-6">
          <pre className="font-mono text-xs sm:text-sm text-green-400/80 leading-tight mb-6 select-none">
            {ASCII_HEADER}
          </pre>
          <div className="space-y-1">
            {bootLines.map((line, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2 }}
                className="font-mono text-xs sm:text-sm"
              >
                <span className="text-green-500/60">$ </span>
                <span className={i === bootLines.length - 1 ? "text-green-400" : "text-green-500/70"}>
                  {line}
                </span>
              </motion.div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            <span className="font-mono text-xs text-green-500/50">loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Main terminal ──
  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-[#050a0f]">
      {/* Scanline overlay */}
      <div className="pointer-events-none fixed inset-0 z-40 opacity-[0.03]"
        style={{
          backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,0,0.1) 2px, rgba(0,255,0,0.1) 4px)",
        }}
      />

      {/* Main chat area */}
      <div className="flex flex-1 flex-col">
        {/* Header bar */}
        <div className="flex items-center justify-between border-b border-green-500/10 bg-[#080d14] px-4 py-2.5">
          <div className="flex items-center gap-3">
            <pre className="font-mono text-[10px] leading-none text-green-500/60 select-none hidden sm:block">
              {">"}_
            </pre>
            <div>
              <h1 className="font-mono text-sm font-bold text-green-400 tracking-wider">
                CYBERSENTINEL // OPERATOR CHANNEL
              </h1>
              <p className="font-mono text-[10px] text-green-500/40">
                ENCRYPTED | REAL-TIME | {onlineUsers.length} OPERATOR{onlineUsers.length !== 1 ? "S" : ""} ONLINE
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowOnline(!showOnline)}
              className="flex items-center gap-1.5 rounded border border-green-500/20 px-2 py-1 font-mono text-[10px] text-green-500/60 transition-colors hover:border-green-500/40 hover:text-green-400 md:hidden"
            >
              <div className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
              ONLINE [{onlineUsers.length}]
            </button>
            <div className="hidden items-center gap-2 md:flex">
              <div className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
              <span className="font-mono text-[10px] text-green-500/50">
                {connected ? "SECURE LINK ACTIVE" : "RECONNECTING..."}
              </span>
            </div>
          </div>
        </div>

        {/* Messages area */}
        <div
          className="flex-1 overflow-y-auto px-4 py-3 space-y-0.5"
          onClick={() => inputRef.current?.focus()}
        >
          {/* Welcome banner */}
          <div className="mb-4 border border-green-500/10 bg-green-500/[0.02] rounded p-3">
            <pre className="font-mono text-[9px] sm:text-[10px] leading-tight text-green-500/30 select-none">
{`╔══════════════════════════════════════════════════════════════╗
║  WELCOME TO CYBERSENTIEL OPERATOR CHANNEL                   ║
║  All messages are end-to-end encrypted.                     ║
║  Solve events appear automatically. Stay sharp, operators.  ║
╚══════════════════════════════════════════════════════════════╝`}
            </pre>
          </div>

          <AnimatePresence initial={false}>
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
              >
                <ChatLine msg={msg} currentUserId={user?.id} />
              </motion.div>
            ))}
          </AnimatePresence>

          {typing && (
            <div className="flex items-center gap-2 py-1">
              <span className="font-mono text-xs text-green-500/40">
                &gt; {typing} is typing
                <span className="animate-pulse">_</span>
              </span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-green-500/10 bg-[#080d14] px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-green-500/60 shrink-0">
              <span className="text-green-400">{user?.username}</span>
              <span className="text-green-500/30">@</span>
              <span className="text-green-500/40">arena</span>
              <span className="text-green-500/30">:{'$'}</span>
            </span>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => handleInput(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={500}
              placeholder="type a message..."
              className="flex-1 bg-transparent font-mono text-sm text-green-300 placeholder-green-700/50 outline-none caret-green-400"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim()}
              className="shrink-0 rounded border border-green-500/30 bg-green-500/10 px-3 py-1 font-mono text-xs text-green-400 transition-all hover:bg-green-500/20 hover:border-green-500/50 disabled:opacity-30 disabled:cursor-not-allowed active:scale-95"
            >
              SEND [ENTER]
            </button>
          </div>
        </div>
      </div>

      {/* Online users sidebar */}
      <div className={`${showOnline ? "translate-x-0" : "translate-x-full"} fixed right-0 top-16 bottom-0 z-30 w-64 border-l border-green-500/10 bg-[#080d14] transition-transform duration-300 md:relative md:translate-x-0`}>
        <div className="border-b border-green-500/10 px-3 py-2.5">
          <h2 className="font-mono text-[11px] font-bold text-green-400/80 tracking-wider">
            OPERATORS ONLINE [{onlineUsers.length}]
          </h2>
        </div>
        <div className="overflow-y-auto p-2 space-y-0.5" style={{ maxHeight: "calc(100vh - 12rem)" }}>
          {onlineUsers.length === 0 && (
            <p className="font-mono text-[10px] text-green-500/30 px-1 py-2">No operators online</p>
          )}
          {onlineUsers.map((u) => (
            <div key={u.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-green-500/5">
              <div className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
              <span className="font-mono text-xs text-green-300 truncate">
                {u.displayName || u.username}
              </span>
              {u.id === user?.id && (
                <span className="font-mono text-[9px] text-green-500/40 shrink-0">(you)</span>
              )}
            </div>
          ))}
        </div>
        {/* Stats */}
        <div className="border-t border-green-500/10 px-3 py-2.5 mt-auto">
          <div className="space-y-1">
            <div className="flex justify-between font-mono text-[10px]">
              <span className="text-green-500/40">MESSAGES</span>
              <span className="text-green-400/60">{messages.filter(m => m.type === "message").length}</span>
            </div>
            <div className="flex justify-between font-mono text-[10px]">
              <span className="text-green-500/40">SOLVES</span>
              <span className="text-green-400/60">{messages.filter(m => m.type === "solve" || m.type === "first_blood").length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      {showOnline && (
        <div className="fixed inset-0 z-20 bg-black/50 md:hidden" onClick={() => setShowOnline(false)} />
      )}
    </div>
  );
}

// ── Chat Line Renderer ──
function ChatLine({ msg, currentUserId }: { msg: ChatMsg; currentUserId?: string }) {
  const time = formatTime(msg.timestamp);

  // ── System / Solve events ──
  if (msg.type === "solve" || msg.type === "first_blood") {
    const isFB = msg.type === "first_blood";
    const cat = msg.meta?.category || "misc";
    const catColor = getCatColor(cat);
    const diff = msg.meta?.difficulty ? getDiffBadge(msg.meta.difficulty) : null;

    return (
      <div className={`flex items-start gap-2 py-1.5 px-2 rounded ${isFB ? "bg-red-500/[0.04] border border-red-500/10" : "bg-green-500/[0.02]"}`}>
        <span className="font-mono text-[10px] text-green-500/30 shrink-0 mt-0.5">{time}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {isFB && (
              <span className="font-mono text-[10px] font-bold text-red-400 bg-red-500/15 px-1.5 py-0.5 rounded shrink-0">
                FIRST BLOOD
              </span>
            )}
            {!isFB && (
              <span className="font-mono text-[10px] font-bold text-green-400 bg-green-500/15 px-1.5 py-0.5 rounded shrink-0">
                SOLVED
              </span>
            )}
            <span className="font-mono text-xs text-green-300">{msg.username}</span>
            <span className="font-mono text-[10px] text-green-500/40">solved</span>
            <span className="font-mono text-xs font-medium" style={{ color: catColor }}>
              [{msg.meta?.challengeTitle || "????"}]
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {diff && (
              <span className={`font-mono text-[9px] px-1 py-0.5 rounded ${diff.bg} ${diff.text}`}>
                {diff.label}
              </span>
            )}
            <span className="font-mono text-[10px] text-green-500/40">
              TIME: <span className={`font-bold ${isFB ? "text-red-400" : "text-green-400"}`}>
                {msg.meta?.solveTime || (msg.meta?.solveTimeMs ? formatSolveTime(msg.meta.solveTimeMs) : "N/A")}
              </span>
            </span>
            {msg.meta?.points != null && (
              <span className="font-mono text-[10px] text-yellow-400/70">
                +{msg.meta.points} pts
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── System messages ──
  if (msg.type === "system" || msg.type === "join") {
    return (
      <div className="flex items-center gap-2 py-1 px-2">
        <span className="font-mono text-[10px] text-green-500/30">{time}</span>
        <span className="font-mono text-[10px] text-green-500/30">***</span>
        <span className="font-mono text-[11px] text-green-500/50 italic">{msg.content}</span>
      </div>
    );
  }

  // ── Regular message ──
  const isMe = msg.userId === currentUserId;
  const roleColors: Record<string, string> = {
    SUPER_ADMIN: "text-red-400",
    ORGANIZER: "text-purple-400",
    AUTHOR: "text-cyan-400",
    PLAYER: "text-green-300",
  };

  return (
    <div className={`flex items-start gap-2 py-1 px-2 rounded transition-colors ${isMe ? "bg-green-500/[0.04]" : "hover:bg-green-500/[0.02]"}`}>
      <span className="font-mono text-[10px] text-green-500/30 shrink-0 mt-0.5">{time}</span>
      <span className="font-mono text-[10px] text-green-500/40 shrink-0 mt-0.5">&gt;</span>
      <div className="flex-1 min-w-0">
        <span className="font-mono text-xs font-bold text-green-400">
          {msg.displayName || msg.username}
        </span>
        <span className="font-mono text-xs text-green-500/30">: </span>
        <span className="font-mono text-xs text-green-200/80 break-words">
          {msg.content}
        </span>
      </div>
    </div>
  );
}
